import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CTAButton } from "../components/CTAButton";
import { useAppraisal } from "../components/AppraisalContext";
import { EditorialMarquee, ServiceGrid, StatsBand, Pillars, ClosingCTA } from "../components/Sections";
import { IMAGES } from "../data";
import { Check, ArrowDown } from "lucide-react";

const headline = ["Your property", "deserves prestigious", "management."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 + i * 0.12 },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden bg-kept-sand" data-testid="hero">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-40 pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="kept-label brass-text mb-8"
          >
            Property Management · New Zealand
          </motion.div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.6rem] font-light tracking-tighter leading-[0.98] text-kept-ink">
            {headline.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="visible">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="text-muted-foreground mt-8 max-w-xl text-base md:text-lg leading-relaxed"
          >
            Comprehensive property management for high-value residential and small commercial
            properties. We handle everything, tenant placement, lease administration, Healthy
            Homes compliance, and ongoing maintenance, so you can protect and grow your
            investment with confidence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <CTAButton testId="hero-book-appraisal" />
            <a href="#services" className="kept-label text-kept-ink/70 link-underline" data-testid="hero-explore">
              Explore our services
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-3"
          >
            {["No tenant placement fees", "Dedicated property manager"].map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm text-kept-ink/80">
                <Check size={16} className="brass-text" strokeWidth={2.5} />
                {b}
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[3/4] overflow-hidden">
            <motion.img
              src={IMAGES.heroExterior}
              alt="Modern architectural property exterior"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-[120%] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-kept-ink/10" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-kept-ink text-kept-sand px-8 py-6 hidden md:block">
            <div className="font-serif text-3xl text-kept-brass">100%</div>
            <div className="kept-label text-kept-sand/70 mt-1" style={{ fontSize: "0.6rem" }}>Healthy Homes Compliant</div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-8 hidden lg:flex items-center gap-2 text-kept-ink/40"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="kept-label" style={{ fontSize: "0.6rem" }}>Scroll</span>
      </motion.div>
    </section>
  );
};

export default function Home() {
  return (
    <div id="services-anchor" data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <div id="services"><ServiceGrid /></div>
      <StatsBand />
      <Pillars />
      <ClosingCTA />
    </div>
  );
}
